import { h } from '@stencil/core';
import type { ObservableMap } from '@stencil/store';

import type { Player, PlayerEvent } from '../../../../../services/interfaces';
import { PlayerEventSubType } from '../../../../../services/interfaces';
import type { MatchHeaderStoreState } from '../../../../../services/interfaces/match-header';
import { isEmpty, omit, translateEntity } from '../../../../../services/utils';
import { isFutsal, getMatchWording } from '../../../match-unit/helpers';
import {
  getPlayerServiceUrl,
  isLiveMatchPenalties,
  matchScorers,
} from '../../../match-unit/helpers/match-unit.helpers';

type Props = {
  state: ObservableMap<MatchHeaderStoreState>['state'];
};

type ScorerPlayer = {
  teamId: string;
  player: Player;
  goals?: GoalEvent[];
};

type GoalEvent = Omit<PlayerEvent, 'player' | 'teamId' | 'teamIdProvider'>;

const MatchScorers = ({ state }: Props): HTMLDivElement => {
  const match = state.matchData;
  if (!match) return null;

  const disableLink: boolean = state.disablePlayerLink;
  const playerServiceUrl: string = state.playerServiceUrl;

  const scorers: { [key: string]: ScorerPlayer[] } = {};
  const matchScoreEvents: PlayerEvent[] = matchScorers(match) ?? [];

  if (!matchScoreEvents.length) return null;

  const printGoalSeconds = isFutsal(match);

  const teams = [{ teamId: match.homeTeam?.id }, { teamId: match.awayTeam?.id }];
  teams.forEach((team) => {
    /**
     * Will hold the information grouped by teamId and player scorer.
     */
    const scorePlayers: ScorerPlayer[] = [];
    /**
     * Parse match `scorers` events and select only events from the same teamId.
     * ! Those score events that are typed as OWN_GOAL will be pushed to the adversary team.
     */
    const scoreEvents: PlayerEvent[] = matchScoreEvents.filter(
      (score) =>
        score.player &&
        (score.teamId === team.teamId) !==
          ([PlayerEventSubType.OWN_GOAL, PlayerEventSubType.OWN] as PlayerEventSubType[]).includes(score.goalType),
    );

    /**
     * Iterate the scorer's event array to take the authoring player information of each goal.
     */
    scoreEvents.forEach((scoreEvent: PlayerEvent) => {
      // Check if the scorePlayers array holds the player information of the score event related.
      const player = scorePlayers.find((p) => p.player.id === scoreEvent.player?.id);
      if (!player) {
        // If there's no player information yet, then add it to the scorePlayers array along with the `teamId`.
        scorePlayers.push({ teamId: scoreEvent.teamId, player: scoreEvent.player });
      }
    });

    /**
     * Iterate the scorer's event array to take the goals event information and assign them to the authoring player.
     */
    scoreEvents?.forEach((scoreEvent: PlayerEvent) => {
      // Check if the scorePlayers array holds the player information of the score event related.
      const player = scorePlayers.find((p) => p.player.id === scoreEvent.player?.id);
      if (!player) return;

      // If the player does not have any goals, create an empty array.
      if (!player.goals?.length) {
        player.goals = [];
      }
      // and add the goal event to the player’s goals array skipping the `player` information, `teamId` and `teamidProvider`.
      const goalEvent = omit(scoreEvent, ['player', 'teamId', 'teamIdProvider']) as GoalEvent;
      player.goals.push({ ...goalEvent });
    });

    scorers[team.teamId] = scorePlayers;
  });

  /**
   * Function that takes a GoalEvent object as an argument and returns a string.
   * @param {GoalEvent} goal - The goal event to check
   * @return {string} The goal’s time in the format <minute'>. Eg: 35'
   */
  const formatOutput = (goal: GoalEvent): string => {
    const penalty_abbr = getMatchWording('penalty_abbr');
    const owngoal_abbr = getMatchWording('owngoal_abbr');
    let output = `${goal.time?.minute}'`;

    if (printGoalSeconds) {
      const seconds = `${goal.time?.second}`.padStart(2, '0');
      output += `${seconds}''`;
    }

    if (goal.time?.injuryMinute) {
      // If the goal happened on injury time, the string will also include <+injuryMinute>. Eg.: 45'+2
      output += `+${goal.time?.injuryMinute}`;
    }

    switch (goal.goalType) {
      case PlayerEventSubType.PENALTY:
      case PlayerEventSubType.PENALTY_GOAL:
        // If the goal is a penalty, the string will include <(penalty_abbr)>. Eg.: 38' (p)
        output += ` (${penalty_abbr})`;
        break;
      case PlayerEventSubType.PENALTY_SECOND_MARK:
        output += ` (2${penalty_abbr})`;
        break;
      case PlayerEventSubType.OWN:
      case PlayerEventSubType.OWN_GOAL:
        // If the goal is a an own gowl, the string will include <owngoal_abbr>. Eg.: 25' og
        output += ` (${owngoal_abbr})`;
        break;
    }
    return output;
  };

  const renderScorerGoals = (scorer: ScorerPlayer) =>
    !!scorer.goals.length && (
      <span class="pk-match-header__scorers--detail">
        {scorer.goals.map((goal: GoalEvent, i: number) => {
          return i === 0 ? ` ${formatOutput(goal)}` : `, ${formatOutput(goal)}`;
        })}
      </span>
    );

  const renderScorerName = (scorer: ScorerPlayer) => (
    <span class="pk-match-header__scorers--player-name" part="scorer-name">
      {translateEntity(scorer.player)?.shortName}
    </span>
  );

  const renderScorerPlayer = (scorer: ScorerPlayer) => {
    // Skip if the player information is not completely available
    // Eg.: translations are missing
    if (isEmpty(scorer.player.translations)) return null;

    return (
      <li part="scorer">
        {!disableLink ? (
          <a href={getPlayerServiceUrl(playerServiceUrl, scorer.player.id, match.competition.id)}>
            {renderScorerName(scorer)}
            {renderScorerGoals(scorer)}
          </a>
        ) : (
          [renderScorerName(scorer), renderScorerGoals(scorer)]
        )}
      </li>
    );
  };

  return (
    <div
      class={{ 'pk-match-header__scorers': true, hidden: isLiveMatchPenalties(match.status, match.phase) }}
      part="events-scorers"
    >
      <div class={{ 'pk-match-header__scorers--home': true }} part="scorers-home">
        <ul class="pk-match-header__scorers--list">
          {match.homeTeam &&
            scorers[match.homeTeam.id].map((scorer) => {
              return renderScorerPlayer(scorer);
            })}
        </ul>
      </div>
      <pk-icon class="[--icon--width:16px] @xs/match-header:[--icon--width:20px]" name="football-goal" title="goal" />
      <div class={{ 'pk-match-header__scorers--away': true }} part="scorers-away">
        <ul class="pk-match-header__scorers--list">
          {match.awayTeam &&
            scorers[match.awayTeam.id].map((scorer) => {
              return renderScorerPlayer(scorer);
            })}
        </ul>
      </div>
    </div>
  );
};

export { MatchScorers };
